import { useEffect, useRef, useState } from "react";

import { Space } from "@prisma/client";
import { useLoaderData, useSearchParams } from "@remix-run/react";
import * as turf from "@turf/turf";
import dayjs from "dayjs";
import { GeoJsonObject } from "geojson";
import L, { LatLng, LatLngBounds } from "leaflet";
import { GeoJSON, Marker, Tooltip, useMapEvents } from "react-leaflet";

import { cn } from "~/lib/utils";
import { SpaceProps } from "~/types";

import { INIT_ZOOM } from "./Map.client";
import DATA from "~/data";

const COLORS = {
    SELLABLE: "#FFC500",
    UNSELLABLE: "#FFE8AF",
    SELECTED: "#3902F6",
    ADDED_TO_CART: "#FCE9A5",
};
export const SELLABLE = "Un-Sold";

export default function Layers({
    spaces,
    selectSpace,
    selectedSpace,
    bounds,
}: {
    spaces: SpaceProps[];
    selectSpace: (product: SpaceProps) => void;
    selectedSpace: SpaceProps | null;
    bounds: LatLngBounds;
}) {
    const { cart } = useLoaderData();

    const [searchParams, setSearchParams] = useSearchParams();
    const [zoom, setZoom] = useState(INIT_ZOOM);

    const spacesWithGeoJson = spaces.filter(({ geojson }) => !!geojson);
    const geoJson = getGeoJsonForSpaces(spacesWithGeoJson);

    const ZOOM_SWITCH = 22;

    const featureClicked = useRef(false);

    const map = useMapEvents({
        click: () => {
            if (!featureClicked.current) {
                selectSpace(null);
            }
            featureClicked.current = false;
        },
        zoomend: () => {
            setZoom(map.getZoom());
        },
    });

    useEffect(() => {
        // EXTRACT - CORONADO + BROOKLAWN - WHAT'S A BETTER SOLUTION FOR SETTING PARAMS ? LINING UP DATA IN CSV?
        if (selectedSpace) {
            if (selectedSpace.propertyType === "NICHE" && !searchParams.get("mausoleumId")) {
                searchParams.set("mausoleumId", selectedSpace[DATA.mausoleums.feature as keyof SpaceProps] as string); // IS THIS GOOD ?
                // searchParams.set("mausoleumId", selectedSpace.lotRowNbrAlpha);
                searchParams.set("spaceId", selectedSpace.lotSellUnitId);
                setSearchParams(searchParams);
            } else {
                const feature = geoJson.features.find(
                    (feature) => feature.properties.lotSellUnitId === selectedSpace.lotSellUnitId,
                );
                if (feature) {
                    const centroid = turf.centroid(feature);
                    const coordinates = centroid.geometry.coordinates;
                    map.flyTo([coordinates[1], coordinates[0]], ZOOM_SWITCH);
                }
            }
        } else {
            map.flyToBounds(bounds);
        }
    }, [selectedSpace, bounds, map]);

    const isInCart = (space) => cart?.spaces.find((cartSpace) => cartSpace.space.lotSellUnitId === space.lotSellUnitId);

    function getColor(properties: any) {
        if (selectedSpace && properties.lotSellUnitId === selectedSpace.lotSellUnitId) {
            return COLORS.SELECTED;
        }

        if (dayjs().isBefore(properties.lockedUntil)) {
            if (isInCart(properties)) {
                return COLORS.ADDED_TO_CART;
            }
            return COLORS.UNSELLABLE;
        }

        switch (properties.salesStatus) {
            case "Un-Sold":
                return COLORS.SELLABLE;
            case "Sold":
            case "Non-Saleable":
            default:
                return COLORS.UNSELLABLE;
        }
    }

    function onSpaceClicked(properties) {
        featureClicked.current = true;
        if (properties.salesStatus !== "Un-Sold") return;
        selectSpace(properties);
    }

    function onEachFeature(feature: any, layer: L.Layer) {
        layer.on({
            click: (e) => onSpaceClicked(feature.properties, [e.latlng.lat, e.latlng.lng]),
        });
    }

    const createCustomIcon = ({ lotLotNbrNum, lotRowNbrAlpha, lotSpaceNbr, lotSellUnitId, salesStatus }) => {
        const isSellable = salesStatus === SELLABLE;
        const title = isSellable
            ? lotRowNbrAlpha
                ? `${lotRowNbrAlpha}-${lotLotNbrNum}`
                : `${lotLotNbrNum}-${lotSpaceNbr}`
            : "";
        return L.divIcon({
            html: `<div class="custom-div-icon ${selectedSpace?.lotSellUnitId === lotSellUnitId ? "selected" : ""
                }">${title}</div>`,
            className: "custom-icon",
            iconSize: [40, 10],
        });
    };

    const spaceLabels = geoJson.features.map((feature, index) => {
        const center = turf.centroid(feature);
        const coordinates = center.geometry.coordinates;

        return (
            <Marker
                key={index}
                position={[coordinates[1], coordinates[0]]}
                icon={createCustomIcon(feature.properties)}
                eventHandlers={{
                    click: (e) => onSpaceClicked(feature.properties, [e.latlng.lat, e.latlng.lng]),
                }}
            >
                {isInCart(feature.properties) ? (
                    <Tooltip direction="top" offset={[0, -10]} opacity={1}>
                        Space added to your cart
                    </Tooltip>
                ) : null}
            </Marker>
        );
    });

    // EXTRACT - CORONADO + BROOKLAWN - IS THIS SCALABLE ? SHOULD DATA BE BETTER LINED UP IN CSV? 
    const mausoleums = spaces
        .filter((space) => space.propertyType === "NICHE")
        .reduce<Record<string, SpaceProps[]>>((acc, feature) => {
            if (acc[feature[DATA.mausoleums.feature as keyof SpaceProps] as string]) {
                acc[feature[DATA.mausoleums.feature as keyof SpaceProps] as string].push(feature);
            } else {
                acc[feature[DATA.mausoleums.feature as keyof SpaceProps] as string] = [feature];
            }
            return acc;
        }, {});

    function onMausoleumClicked(id) {
        featureClicked.current = true;
        searchParams.set("mausoleumId", id);
        searchParams.delete("spaceId");

        setSearchParams(searchParams);
    }

    return (
        <>
            {zoom > 20 ? spaceLabels : null}
            <GeoJSON
                key={JSON.stringify(spaces)}
                data={geoJson as GeoJsonObject}
                style={({ properties }) => {
                    const { salesStatus, isFiltered } = properties;
                    const isSellable = salesStatus === SELLABLE;
                    return {
                        color: isSellable ? "#FFFFFF" : COLORS.UNSELLABLE,
                        weight: 1,
                        opacity: isSellable ? 1 : 0.1,
                        fillColor: isFiltered ? getColor(properties) : "#FFE8AF",
                        fillOpacity: isSellable ? 1 : 0.1,
                    };
                }}
                onEachFeature={onEachFeature}
            />
            {Object.entries(mausoleums).map(([key, value], index) => (
                <Marker
                    key={key}
                    position={[DATA.mausoleums.coords[key].lat, DATA.mausoleums.coords[key].lng]}
                    title="key"
                    icon={createMausoleumIcon(value)}
                    eventHandlers={{
                        click: () => onMausoleumClicked(key),
                    }}
                />
            ))}
        </>
    );
}

export function getGeoJsonForSpaces(spaces) {
    return {
        type: "FeatureCollection",
        features: spaces.map(({ geojson, ...properties }) => {
            // todo: could reprocess json to be just an array of points. no lat/lng or outer array?
            const coordinates = geojson[0]?.map(({ lat, lng }) => [lng, lat]);
            return {
                type: "Feature",
                geometry: {
                    type: "Polygon",
                    coordinates: [coordinates],
                },
                properties,
            };
        }),
    };
}

const createMausoleumIcon = function (cluster: (Space & { isFiltered: boolean })[]) {
    return L.divIcon({
        html: `<span>${cluster.length}</span>`,
        className: cn(
            "custom-marker-cluster",
            cluster.filter(({ isFiltered }) => isFiltered).length > 0 ? "bg-primary" : "bg-primary-100",
        ),
        iconSize: L.point(33, 33, true),
    });
};
